import React, { useState, useEffect, useRef } from 'react';
import { Button, Typography, CircularProgress, TextField, LinearProgress, Box, Select, MenuItem, FormControl, InputLabel, useMediaQuery, useTheme, Dialog, DialogTitle, DialogContent, DialogActions, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';
import * as XLSX from 'xlsx';
import axios from 'axios';
import { chunk } from 'lodash';
import './App.css';
import translations from './localizations';
import { GoogleGenerativeAI } from "@google/generative-ai";

function TranslatePage({ language }) {
  const [file, setFile] = useState(null);
  const [data, setData] = useState(null);
  const [translating, setTranslating] = useState(false);
  const [translated, setTranslated] = useState(false);
  const [isPasswordProtected, setIsPasswordProtected] = useState(false);
  const [password, setPassword] = useState('');
  const [processingFile, setProcessingFile] = useState(false);
  const [hasTranslationErrors, setHasTranslationErrors] = useState(false);
  const [progress, setProgress] = useState(0);
  const [worker, setWorker] = useState(null);
  const BATCH_SIZE = 100; // Number of rows in each batch
  const [selectedRow, setSelectedRow] = useState(null);
  const [rowNumber, setRowNumber] = useState('');
  const [hasVietnameseTranslation, setHasVietnameseTranslation] = useState(false);
  const [untranslatedRows, setUntranslatedRows] = useState([]);
  const [selectedModel, setSelectedModel] = useState('gpt-4o-mini');
  const [geminiApiKey, setGeminiApiKey] = useState(process.env.REACT_APP_GEMINI_API_KEY || '');
  const [systemInstruction, setSystemInstruction] = useState(() => {
    return localStorage.getItem('systemInstruction') || 
      `Bạn là một chuyên gia dịch thuật từ ngôn ngữ Hàn sang Việt. Hãy dịch các câu tiếng Hàn sang tiếng Việt theo các yêu cầu sau, chỉ in ra phần dịch tiếng Hàn: 
1.	Giữ nguyên ý nghĩa chính của câu. Không thêm hoặc bớt thông tin.
2.	Nếu là đoạn văn, hãy giữ nguyên cấu trúc đoạn văn, không gộp câu hoặc tách câu.
3.	Điều chỉnh cấu trúc câu cho phù hợp với ngữ pháp tiếng Việt. Chia nhỏ câu dài nếu cần, nhưng giữ mạch liên kết và không tách thành 2 câu.
4.	Giữ nguyên bối cảnh giữa các dòng nếu các câu văn có thể có mối liên quan đến nhau (đến từ một đoạn văn, một bài báo …) 
5.	Sử dụng từ vựng thuần Việt thay thế cho từ gốc nước ngoài khi có thể.
6.	Áp dụng cách diễn đạt tự nhiên: 
-	Sử dụng thành ngữ, tục ngữ tiếng Việt phù hợp.
-	Thêm trợ từ, tiểu từ như "mà", "thì", "đấy", "ạ," …để câu tự nhiên hơn.
7.	Giữ nguyên sắc thái và giọng điệu của câu gốc.
8.	Sử dụng những từ ngữ phổ biến, không sử dụng các từ vùng miền, từ lóng, viết tắt …
9.	Không xoá các ký tự đặc biệt trong câu (🌷🚗🌟☕…)
10.	Xử lý thuật ngữ chuyên ngành: 
-	Giữ nguyên nếu không có từ tương đương.
-	Không sử dụng các dấu ngoặc đơn, ngoặc kép để chú thích nghĩa của từ, nếu thuật ngữ gốc không có từ tương đưong, hãy giữ nguyên từ đó trong câu.
11.	Duy trì tính chính xác của số liệu, đơn vị đo lường, và tên riêng.
12.	Giữ nguyên các yếu tố văn hóa đặc trưng.
13.	Điều chỉnh dấu câu theo quy tắc tiếng Việt.
14.	Sử dụng từ nối phù hợp để giữ mạch logic.
15.	Tôn trọng phong cách và giọng điệu của văn bản gốc.
16.	Chuyển các cách nói địa phương sang tiếng Việt phổ thông nếu có thể.
17.	Đảm bảo tính nhất quán trong việc dịch thuật ngữ và cách xưng hô.
18.	Tránh dịch quá sát nghĩa từng từ. Ưu tiên diễn đạt ý tưởng tổng thể.
19.	Xử lý thành ngữ, tục ngữ: 
-	Sử dụng thành ngữ, tục ngữ tương đương trong tiếng Việt.
-	Diễn giải ý nghĩa nếu không có tương đưong.
20.	Sử dụng đại từ nhân xưng thống nht: 
-	Chuyển tất cả đại từ ngôi thứ nhất thành "tôi".
-	Sử dụng "bạn" cho tất cả đại từ ngôi thứ hai.
-	Giữ nguyên đại từ ngôi thứ ba.
21.	Điều chỉnh động từ phù hợp với đại từ nhân xưng đã thay đổi.
22.	Trong trích dẫn trực tiếp hoặc văn bản chính thức, có thể giữ nguyên cách xưng hô gốc nếu cần thiết.
Khi thực hiện nhiệm vụ, hãy làm theo các bước sau:
1.	Đọc kỹ câu gốc và xác định ý nghĩa chính.
2.	Xác định các yếu tố cần điều chỉnh (cấu trúc, từ vựng, đại từ nhân xưng).
3.	Áp dụng các quy tắc trên để diễn đạt lại câu.
4.	Kiểm tra lại câu đã diễn đạt để đảm bảo giữ nguyên ý nghĩa và tính tự nhiên.
`;
  });
  const [openSystemInstruction, setOpenSystemInstruction] = useState(false);

  const [problematicRows, setProblematicRows] = useState([]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const fileInputRef = useRef(null);

  useEffect(() => {
    const newWorker = new Worker(new URL('./translationWorker.js', import.meta.url));
    setWorker(newWorker);

    return () => {
      newWorker.terminate();
    };
  }, []);

  useEffect(() => {
    localStorage.setItem('systemInstruction', systemInstruction);
  }, [systemInstruction]);

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    setFile(file);
    setProcessingFile(true);
    setTranslated(false); // Reset trạng thái translated khi chọn file mới
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const workbook = XLSX.read(e.target.result, { type: 'binary' });
        processWorkbook(workbook);
      } catch (error) {
        if (error.message.includes('Password')) {
          setIsPasswordProtected(true);
        } else {
          console.error('Error reading file:', error);
        }
        setProcessingFile(false);
      }
    };
    reader.readAsBinaryString(file);
  };

  const processWorkbook = (workbook) => {
    const sheetName = workbook.SheetNames[0];
    const sheet = workbook.Sheets[sheetName];
    const jsonData = XLSX.utils.sheet_to_json(sheet);

    const hasVietnamese = jsonData.some(row => row['Vietnamese'] && row['Vietnamese'].trim() !== '');
    setHasVietnameseTranslation(hasVietnamese);

    if (hasVietnamese) {
      const untranslated = jsonData.filter(row => !row['Vietnamese'] || row['Vietnamese'].trim() === '');
      setUntranslatedRows(untranslated);
    }

    setData(jsonData);
    setProcessingFile(false);
  };

  const handlePasswordInput = (event) => {
    setPassword(event.target.value);
  };

  const tryOpenWithPassword = () => {
    setProcessingFile(true);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const workbook = XLSX.read(e.target.result, { type: 'binary', password: password });
        processWorkbook(workbook);
        setIsPasswordProtected(false);
      } catch (error) {
        console.error('Error reading file with password:', error);
        setProcessingFile(false);
      }
    };
    reader.readAsBinaryString(file);
  };

  const translateDataInBatches = async () => {
    if (!data || data.length === 0) return;

    setTranslating(true);
    setProgress(0);
    setHasTranslationErrors(false);
    const apiKey = process.env.REACT_APP_OPENAI_API_KEY;
    const openRouterApiKey = process.env.REACT_APP_OPENROUTER_API_KEY;

    const batches = chunk(data, BATCH_SIZE);
    const totalBatches = batches.length;
    const updatedData = [];
    const newProblematicRows = [];

    for (let i = 0; i < totalBatches; i++) {
      const startIndex = i * BATCH_SIZE;
      try {
        const result = await new Promise((resolve, reject) => {
          worker.onmessage = (e) => {
            if (e.data.type === 'progress') {
              setProgress(e.data.progress);
            } else if (e.data.type === 'success') {
              resolve(e.data.data);
            } else if (e.data.type === 'error') {
              reject(new Error(e.data.error));
            }
          };

          worker.postMessage({
            batch: batches[i],
            apiKey,
            geminiApiKey,
            openRouterApiKey,
            columnToTranslate: 'Korean',
            startIndex,
            model: selectedModel,
            systemInstruction,
            totalBatches,
            currentBatch: i + 1
          });
        });

        updatedData.push(...result);

        // Kiểm tra các dòng có vấn đề
        result.forEach((row, index) => {
          if (row.TranslationStatus === 'Error' || row.TranslationStatus === 'Contains Korean') {
            newProblematicRows.push({
              rowNumber: startIndex + index + 1,
              status: row.TranslationStatus,
              korean: row.Korean,
              vietnamese: row.Vietnamese
            });
          }
        });

      } catch (error) {
        console.error(`Error in batch ${i + 1}:`, error);
        setHasTranslationErrors(true);
        handleTranslationError(error);
      }
    }

    setData(updatedData);
    setProblematicRows(newProblematicRows);
    setTranslating(false);
    setTranslated(true);
    setProgress(100);
  };

  const handleTranslationError = (error) => {
    console.error('Translation error:', error);
    // You can add more error handling logic here
  };

  const exportToExcel = () => {
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Translated");
    XLSX.writeFile(wb, "translated_data.xlsx");
  };

  const handleRowNumberChange = (event) => {
    setRowNumber(event.target.value);
  };

  const handleViewRow = () => {
    const rowIndex = parseInt(rowNumber) - 1;
    if (rowIndex >= 0 && rowIndex < data.length) {
      setSelectedRow(data[rowIndex]);
    } else {
      setSelectedRow(null);
    }
  };

  const t = (key, params = {}) => {
    const translation = translations[language][key];
    if (typeof translation === 'string') {
      return Object.entries(params).reduce(
        (str, [key, value]) => str.replace(`{${key}}`, value),
        translation
      );
    }
    return key;
  };

  const handleReset = () => {
    setFile(null);
    setData(null);
    setTranslating(false);
    setTranslated(false);
    setIsPasswordProtected(false);
    setPassword('');
    setProcessingFile(false);
    setHasTranslationErrors(false);
    setProgress(0);
    setSelectedRow(null);
    setRowNumber('');
    setHasVietnameseTranslation(false);
    setUntranslatedRows([]);
    setProblematicRows([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleOpenSystemInstruction = () => {
    setOpenSystemInstruction(true);
  };

  const handleCloseSystemInstruction = () => {
    setOpenSystemInstruction(false);
    localStorage.setItem('systemInstruction', systemInstruction);
  };

  const handleModelChange = (event) => {
    setSelectedModel(event.target.value);
  };

  return (
    <Box>
      <Box className="language-selector">
        <Button
          variant="outlined"
          size="small"
          onClick={handleOpenSystemInstruction}
        >
          System instruction
        </Button>

        <FormControl variant="outlined" size="small">
          <InputLabel id="model-select-label">{t('model')}</InputLabel>
          <Select
            labelId="model-select-label"
            id="model-select"
            value={selectedModel}
            onChange={handleModelChange}
            label={t('model')}
          >
            <MenuItem value="gpt-4o">GPT-4o</MenuItem>
            <MenuItem value="gpt-4o-mini">GPT-4o-mini</MenuItem>
            {/* <MenuItem value="openai/gpt-4o">GPT-4o</MenuItem> */}
            {/* <MenuItem value="openai/gpt-4o-mini">GPT-4o-mini</MenuItem> */}
            {/* <MenuItem value="nvidia/llama-3.1-nemotron-70b-instruct">Nvidia: Llama 3.1 Nemotron 70B Instruct</MenuItem> */}
            {/* <MenuItem value="google/gemini-flash-1.5">Google: Gemini Flash 1.5</MenuItem> */}
            <MenuItem value="google/gemini-2.0-flash-001">Google: Gemini Flash 2.0</MenuItem>
            <MenuItem value="google/gemini-pro-1.5">Google: Gemini Pro 1.5</MenuItem>
            <MenuItem value="deepseek/deepseek-r1">DeepSeek: R1</MenuItem>
            <MenuItem value="deepseek/deepseek-r1-distill-llama-70b">DeepSeek: R1 Distill Llama 70B</MenuItem>
            <MenuItem value="anthropic/claude-3.5-sonnet">Anthropic: Claude 3.5 Sonnet</MenuItem>
            <MenuItem value="eva-unit-01/eva-qwen-2.5-72b">Qwen2.5 72B</MenuItem>
            {/* <MenuItem value="meta-llama/llama-3.1-70b-instruct">Meta: Llama 3.1 70B Instruct</MenuItem> */}
            {/* <MenuItem value="meta-llama/llama-3.2-90b-vision-instruct">Meta: Llama 3.2 90B Vision Instruct</MenuItem> */}
          </Select>
        </FormControl>
      </Box>

      <Typography variant="h4" gutterBottom align="center" style={{ marginTop: '100px' }}>
        {t('title')}
      </Typography>
      <Box className="center-content">
        <div className="button-container">
          <input
            type="file"
            accept=".xlsx, .xls"
            onChange={handleFileUpload}
            style={{ display: 'none' }}
            id="file-upload"
            ref={fileInputRef}
          />
          <label htmlFor="file-upload">
            <Button variant="contained" component="span" disabled={processingFile} className="custom-button">
              {processingFile ? <CircularProgress size={24} /> : t('uploadButton')}
            </Button>
          </label>
          {file && (
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleReset}
              className="reset-button custom-button"
            >
              {t('resetButton')}
            </Button>
          )}
        </div>
        {file && !processingFile && (
          <Typography variant="body1" gutterBottom align="center">
            {t('fileUploaded')} {file.name}
            {hasVietnameseTranslation && (
              <Typography color="primary" align="center">
                {t('hasTranslations')}
              </Typography>
            )}
            {hasVietnameseTranslation && untranslatedRows.length > 0 && (
              <Typography color="error" align="center">
                {t('untranslatedRows', { count: untranslatedRows.length })}
              </Typography>
            )}
          </Typography>
        )}
      </Box>
      {isPasswordProtected && (
        <Box mt={2} display="flex" flexDirection={isMobile ? 'column' : 'row'} alignItems={isMobile ? 'stretch' : 'center'} justifyContent="center">
          <Typography variant="body1" color="error" gutterBottom align="center">
            {t('passwordProtected')}
          </Typography>
          <TextField
            type="password"
            label={t('passwordLabel')}
            variant="outlined"
            value={password}
            onChange={handlePasswordInput}
            disabled={processingFile}
            fullWidth={isMobile}
            style={isMobile ? { marginBottom: '1rem' } : { marginRight: '1rem' }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={tryOpenWithPassword}
            disabled={processingFile}
            fullWidth={isMobile}
          >
            {processingFile ? <CircularProgress size={24} /> : t('openFileButton')}
          </Button>
        </Box>
      )}
      {data && (
        <Box className="center-content">
          {!translated ? (
            <Button
              variant="contained"
              color="primary"
              onClick={translateDataInBatches}
              disabled={translating}
              style={{ marginTop: '20px' }}
            >
              {translating ? <CircularProgress size={24} /> : t('translateButton')}
            </Button>
          ) : (
            <Button
              variant="contained"
              color="secondary"
              onClick={exportToExcel}
              style={{ marginTop: '20px' }}
            >
              {t('exportButton')}
            </Button>
          )}
        </Box>
      )}
      {translating && (
        <div style={{ marginTop: '20px' }}>
          <LinearProgress variant="determinate" value={progress} />
          <Typography variant="body2" color="textSecondary">
            {`${Math.round(progress)}%`}
          </Typography>
        </div>
      )}
      
      {data && (
        <Box mt={4} display="flex" flexDirection={isMobile ? 'column' : 'row'} alignItems="center" justifyContent="center">
          <TextField
            type="number"
            label={t('enterRowNumber')}
            variant="outlined"
            value={rowNumber}
            onChange={handleRowNumberChange}
            fullWidth={isMobile}
            style={isMobile ? { marginBottom: '1rem' } : { marginRight: '1rem' }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleViewRow}
            fullWidth={isMobile}
          >
            {t('viewRowButton')}
          </Button>
        </Box>
      )}
      
      {selectedRow && (
        <Box mt={2} border={1} borderColor="grey.300" p={2} className="center-content">
          <Typography variant="h6" align="center">{t('contentOfRow', { number: rowNumber })}</Typography>
          <Typography align="center"><strong>{t('korean')}</strong> {selectedRow['Korean']}</Typography>
          <Typography align="center"><strong>{t('vietnamese')}</strong> {selectedRow['Vietnamese'] || t('notTranslated')}</Typography>
        </Box>
      )}

      <Dialog open={openSystemInstruction} onClose={handleCloseSystemInstruction} maxWidth="md" fullWidth>
        <DialogTitle>System Instruction</DialogTitle>
        <DialogContent>
          <TextField
            multiline
            rows={10}
            fullWidth
            variant="outlined"
            value={systemInstruction}
            onChange={(e) => setSystemInstruction(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSystemInstruction} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {translated && (
        <Box mt={4}>
          <Typography variant="h6" gutterBottom>
            {t('problematicRows')}
          </Typography>
          {problematicRows.length > 0 ? (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Row Number</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Korean</TableCell>
                    <TableCell>Vietnamese</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {problematicRows.map((row, index) => (
                    <TableRow key={index}>
                      <TableCell>{row.rowNumber}</TableCell>
                      <TableCell>{row.status}</TableCell>
                      <TableCell>{row.korean}</TableCell>
                      <TableCell>{row.vietnamese}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography>{t('noProblematicRowsFound')}</Typography>
          )}
        </Box>
      )}
    </Box>
  );
}

export default TranslatePage;
